import type { MeetingDetail } from '@wundo/shared/calendar.ts';

/** Up to this many are named on the hero line; past it, three are. */
const NAMES_ON_THE_LINE = 4;
const NAMES_WHEN_THERE_ARE_MORE = 3;

/** One guest, as the hero line and the dialog show them. */
export interface MeetingAttendeeRow {
  /** What to call them. Resolved by the backend; never empty. */
  label: string;
  /**
   * The address they were invited at, or null when the calendar sent none. It
   * may be the label itself — whoever shows both decides whether that is worth
   * repeating.
   */
  email: string | null;
  organizer: boolean;
}

export interface MeetingAttendeeList {
  /** Everyone, organiser first — what the dialog lists. */
  all: MeetingAttendeeRow[];
  /** The ones the hero line names, in the same order. */
  named: MeetingAttendeeRow[];
  /** How many the line leaves to the dialog. Zero when it names them all. */
  hidden: number;
}

/**
 * Who is coming, ordered for reading rather than as the calendar sent it.
 *
 * Who anybody *is* — their name, or their address when no name is known — is
 * the backend's answer, already on every attendee. This only decides the
 * order and the cut: the organiser goes first and stays marked, because on a
 * meeting whose guest list nobody can read they are the one name that says
 * what it is. Past a handful of guests the line stops naming people and says
 * how many are left — two hundred names in a hero bar is not a list, it is a
 * wall.
 *
 * A meeting with no guests at all gets nothing, not even the organiser: an
 * appointment with yourself has no attendee line to fill.
 */
export function meetingAttendeeList(meeting: MeetingDetail): MeetingAttendeeList {
  const guests: MeetingAttendeeRow[] = meeting.attendees.map((attendee) => ({
    label: attendee.label,
    email: attendee.email?.trim() || null,
    organizer: false,
  }));

  if (guests.length === 0) {
    return { all: [], named: [], hidden: 0 };
  }

  const all = organizerFirst(guests, meeting);
  if (all.length <= NAMES_ON_THE_LINE) {
    return { all, named: all, hidden: 0 };
  }
  return {
    all,
    named: all.slice(0, NAMES_WHEN_THERE_ARE_MORE),
    hidden: all.length - NAMES_WHEN_THERE_ARE_MORE,
  };
}

/*
 * The organiser is usually on the guest list too, and then this is a move
 * rather than an addition — the same person twice would read as two people.
 * Calendars invite by address, so the e-mail is what they are matched on;
 * display names are not unique and are not always filled in.
 */
function organizerFirst(
  guests: MeetingAttendeeRow[],
  meeting: MeetingDetail,
): MeetingAttendeeRow[] {
  const address = normalise(meeting.organizer_email);
  const at = address
    ? guests.findIndex((guest) => normalise(guest.email) === address)
    : -1;

  if (at >= 0) {
    const organizer: MeetingAttendeeRow = { ...guests[at], organizer: true };
    return [organizer, ...guests.slice(0, at), ...guests.slice(at + 1)];
  }

  /* An organiser who did not invite themselves still opens the list. */
  if (meeting.organizer_label === null) return guests;
  return [
    {
      label: meeting.organizer_label,
      email: meeting.organizer_email?.trim() || null,
      organizer: true,
    },
    ...guests,
  ];
}

function normalise(email: string | null): string {
  return email?.trim().toLocaleLowerCase() ?? '';
}
